import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import HiveLocationMap from "@/components/HiveLocationMap";
import { SPECIES_OPTIONS, SPECIES_VALUES, SPECIES_OTHER } from "@/lib/species";
import { Crosshair, Search, MapPin } from "lucide-react";
import { toast } from "sonner";

const emptyHive = {
  name: "",
  location: "",
  latitude: null,
  longitude: null,
  hive_type: "Langstroth",
  species: "",
  queen_color: "",
  queen_year: "",
  installation_date: "",
  strength: "moderate",
  notes: "",
  yard: "",
  color: "#EAB308",
  tags: [],
};

const COLOR_SWATCHES = [
  "#EAB308", // yellow
  "#F59E0B", // amber
  "#10B981", // emerald
  "#3B82F6", // blue
  "#A855F7", // purple
  "#EC4899", // pink
  "#F97316", // orange
  "#78716C", // stone
];

export default function HiveFormDialog({ hive, onSaved }) {
  const [form, setForm] = useState(
    hive
      ? {
          ...emptyHive,
          ...hive,
          latitude: hive.latitude ?? null,
          longitude: hive.longitude ?? null,
          queen_year: hive.queen_year ?? "",
          installation_date: hive.installation_date ?? "",
          tags: hive.tags || [],
          color: hive.color || emptyHive.color,
        }
      : emptyHive
  );
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [showMap, setShowMap] = useState(
    Boolean(hive && (hive.latitude != null || hive.longitude != null))
  );

  const setCoords = (lat, lon, label) => {
    setForm((f) => ({
      ...f,
      latitude: lat == null ? null : Number(lat.toFixed(5)),
      longitude: lon == null ? null : Number(lon.toFixed(5)),
      ...(label ? { location: label } : {}),
    }));
  };

  const detectMyLocation = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Geolocation isn't available in this browser");
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const res = await api.get("/weather/reverse-geocode", { params: { lat: latitude, lon: longitude } });
          setCoords(res.data.latitude, res.data.longitude, res.data.label || form.location);
          setShowMap(true);
          toast.success(res.data.label ? `Pinned: ${res.data.label}` : "Coordinates pinned");
        } catch {
          // Even without a label, we still got coords
          setCoords(latitude, longitude);
          setShowMap(true);
          toast.success("Coordinates pinned");
        } finally {
          setDetecting(false);
        }
      },
      (err) => {
        setDetecting(false);
        toast.error(err.code === 1
          ? "Location permission denied. You can still type an address or pin on the map."
          : "Couldn't detect your location. Try typing an address instead.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const lookupAddress = async () => {
    const q = (form.location || "").trim();
    if (q.length < 2) {
      toast.error("Type a city or address first");
      return;
    }
    setGeocoding(true);
    try {
      const res = await api.get("/weather/geocode", { params: { q } });
      setCoords(res.data.latitude, res.data.longitude, res.data.label || q);
      setShowMap(true);
      toast.success(`Found: ${res.data.label}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't find that location");
    } finally {
      setGeocoding(false);
    }
  };

  const addTag = (raw) => {
    // Mirror backend rules: max 40 chars, alphanum + space/_/-, max 30 tags.
    const t = raw.trim().toLowerCase().replace(/[^a-z0-9 _-]/g, "");
    if (!t || t.length > 40) return;
    if (form.tags.includes(t)) return;
    if (form.tags.length >= 30) return;
    setForm({ ...form, tags: [...form.tags, t] });
    setTagInput("");
  };
  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter((x) => x !== t) });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        queen_year: form.queen_year ? Number(form.queen_year) : null,
        installation_date: form.installation_date || null,
        latitude: form.latitude == null || form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude == null || form.longitude === "" ? null : Number(form.longitude),
      };
      const res = hive
        ? await api.patch(`/hives/${hive.id}`, payload)
        : await api.post("/hives", payload);
      toast.success(hive ? "Hive updated" : "Hive added");
      onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium">
          {hive ? "Edit hive" : "Add hive"}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Name</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Linden, Maple..."
            maxLength={200}
            data-testid="hive-name-input"
          />
        </div>
        {/* ───── Location block (address + GPS + map) ───── */}
        <div className="rounded-lg border border-stone-200 bg-stone-50/50 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-1.5 text-stone-700">
              <MapPin className="w-4 h-4" /> Hive location
            </Label>
            <span className="text-[10px] uppercase tracking-wider text-stone-400">
              powers weather & reminders
            </span>
          </div>
          <div className="flex gap-2">
            <Input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupAddress(); } }}
              placeholder="City, address, or landmark"
              maxLength={200}
              data-testid="hive-location-input"
              className="bg-white"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={lookupAddress}
              disabled={geocoding || !form.location?.trim()}
              title="Find on map"
              data-testid="hive-location-lookup"
              className="shrink-0"
            >
              <Search className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={detectMyLocation}
              disabled={detecting}
              title="Use my current location"
              data-testid="hive-location-detect"
              className="shrink-0"
            >
              <Crosshair className={`w-4 h-4 ${detecting ? "animate-pulse" : ""}`} />
            </Button>
          </div>
          {(showMap || (form.latitude != null && form.longitude != null)) && (
            <>
              <HiveLocationMap
                latitude={form.latitude}
                longitude={form.longitude}
                onChange={async (lat, lon) => {
                  setCoords(lat, lon);
                  // Best-effort reverse geocode so the label stays in sync
                  try {
                    const res = await api.get("/weather/reverse-geocode", { params: { lat, lon } });
                    if (res.data.label) setForm((f) => ({ ...f, location: res.data.label }));
                  } catch { /* silent */ }
                }}
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-stone-500">Latitude</Label>
                  <Input
                    type="number"
                    step="0.00001"
                    min={-90}
                    max={90}
                    value={form.latitude ?? ""}
                    onChange={(e) => setForm({ ...form, latitude: e.target.value === "" ? null : Number(e.target.value) })}
                    placeholder="—"
                    data-testid="hive-latitude-input"
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-500">Longitude</Label>
                  <Input
                    type="number"
                    step="0.00001"
                    min={-180}
                    max={180}
                    value={form.longitude ?? ""}
                    onChange={(e) => setForm({ ...form, longitude: e.target.value === "" ? null : Number(e.target.value) })}
                    placeholder="—"
                    data-testid="hive-longitude-input"
                    className="bg-white"
                  />
                </div>
              </div>
              <p className="text-xs text-stone-500">
                Click the map or drag the pin to adjust. We'll use these coordinates for weather forecasts and seasonal reminders.
              </p>
            </>
          )}
          {!showMap && form.latitude == null && (
            <button
              type="button"
              onClick={() => setShowMap(true)}
              className="text-xs text-amber-700 hover:underline"
              data-testid="hive-location-show-map"
            >
              Pin manually on map →
            </button>
          )}
        </div>
        <div>
          <Label>Type</Label>
          <Select value={form.hive_type} onValueChange={(v) => setForm({ ...form, hive_type: v })}>
            <SelectTrigger data-testid="hive-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Langstroth">Langstroth</SelectItem>
              <SelectItem value="Top Bar">Top Bar</SelectItem>
              <SelectItem value="Warre">Warré</SelectItem>
              <SelectItem value="Dadant">Dadant</SelectItem>
              <SelectItem value="Flow Hive">Flow Hive</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <SpeciesField form={form} setForm={setForm} />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Queen color</Label>
            <Input
              value={form.queen_color}
              onChange={(e) => setForm({ ...form, queen_color: e.target.value })}
              placeholder="Yellow, White..."
              maxLength={60}
              data-testid="hive-queen-color-input"
            />
          </div>
          <div>
            <Label>Queen year</Label>
            <Input
              type="number"
              min={1900}
              max={2100}
              value={form.queen_year}
              onChange={(e) => setForm({ ...form, queen_year: e.target.value })}
              placeholder="2024"
              data-testid="hive-queen-year-input"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Installed</Label>
            <Input
              type="date"
              value={form.installation_date || ""}
              onChange={(e) => setForm({ ...form, installation_date: e.target.value })}
              data-testid="hive-installed-input"
            />
          </div>
          <div>
            <Label>Strength</Label>
            <Select value={form.strength} onValueChange={(v) => setForm({ ...form, strength: v })}>
              <SelectTrigger data-testid="hive-strength-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weak">Weak</SelectItem>
                <SelectItem value="moderate">Moderate</SelectItem>
                <SelectItem value="strong">Strong</SelectItem>
                <SelectItem value="thriving">Thriving</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            maxLength={10000}
            data-testid="hive-notes-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Yard / apiary</Label>
            <Input
              value={form.yard}
              onChange={(e) => setForm({ ...form, yard: e.target.value })}
              placeholder="Back garden"
              maxLength={200}
              data-testid="hive-yard-input"
            />
          </div>
          <div>
            <Label>Color</Label>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              {COLOR_SWATCHES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, color: c })}
                  className={`w-6 h-6 rounded-full border-2 ${form.color === c ? "border-stone-900" : "border-white"} shadow-sm`}
                  style={{ background: c }}
                  data-testid={`hive-color-${c}`}
                  aria-label={`color ${c}`}
                />
              ))}
            </div>
          </div>
        </div>
        <div>
          <Label>Tags</Label>
          <div className="flex flex-wrap gap-1.5 mt-1.5 mb-2">
            {form.tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 text-xs bg-stone-100 text-stone-700 rounded-full px-2 py-0.5">
                {t}
                <button type="button" onClick={() => removeTag(t)} className="text-stone-400 hover:text-red-600" data-testid={`tag-remove-${t}`}>×</button>
              </span>
            ))}
          </div>
          <Input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagInput); }
            }}
            onBlur={() => tagInput && addTag(tagInput)}
            placeholder="research, queenless, urgent…"
            maxLength={40}
            disabled={form.tags.length >= 30}
            data-testid="hive-tag-input"
          />
        </div>
      </div>
      <DialogFooter>
        <Button
          onClick={save}
          disabled={saving || !form.name}
          className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300"
          data-testid="hive-save-button"
        >
          {saving ? "Saving…" : "Save hive"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}


function SpeciesField({ form, setForm }) {
  // Detect "is custom value?" mode — when editing a hive whose existing
  // species isn't in our preset list, default to "Other…" with a free-text input.
  const isCustom = !!form.species && !SPECIES_VALUES.includes(form.species);
  return (
    <div>
      <Label>Species</Label>
      <Select
        value={isCustom ? SPECIES_OTHER : (form.species || "")}
        onValueChange={(v) => {
          if (v === SPECIES_OTHER) {
            // Switch to free-text mode — keep any existing custom value
            setForm({ ...form, species: form.species && !SPECIES_VALUES.includes(form.species) ? form.species : " " });
          } else {
            setForm({ ...form, species: v });
          }
        }}
      >
        <SelectTrigger data-testid="hive-species-select">
          <SelectValue placeholder="Select species (optional)" />
        </SelectTrigger>
        <SelectContent>
          {SPECIES_OPTIONS.map((g) => (
            <SelectGroup key={g.group}>
              <SelectLabel className="text-[10px] uppercase tracking-wider text-stone-500">{g.group}</SelectLabel>
              {g.items.map((i) => (
                <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
              ))}
            </SelectGroup>
          ))}
          <SelectGroup>
            <SelectLabel className="text-[10px] uppercase tracking-wider text-stone-500">Custom</SelectLabel>
            <SelectItem value={SPECIES_OTHER}>Other…</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
      {isCustom && (
        <Input
          autoFocus
          value={form.species.trim() === "" ? "" : form.species}
          onChange={(e) => setForm({ ...form, species: e.target.value })}
          placeholder="e.g. local ecotype, breeder line…"
          maxLength={80}
          className="mt-2"
          data-testid="hive-species-custom-input"
        />
      )}
    </div>
  );
}
